import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Shield, Pencil, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProtectionOrderRecord {
  id: number;
  person_id: number;
  agency_id: number;
  order_type: string | null;
  filed_date: string | null;
  granted_date: string | null;
  expiration_date: string | null;
  status: string | null;
  details: string | null;
  staff_assisted_id: number | null;
  staff_first_name?: string;
  staff_last_name?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const baseOrderTypeOptions = [
  { value: "temporary", label: "Temporary" },
  { value: "final", label: "Final" },
  { value: "emergency", label: "Emergency" },
  { value: "stalking", label: "Stalking" },
  { value: "sexual_assault", label: "Sexual Assault" },
  { value: "protective_order", label: "Protective Order" },
  { value: "restraining_order", label: "Restraining Order" },
  { value: "other", label: "Other" },
];

const baseStatusOptions = [
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "granted", label: "Granted" },
  { value: "denied", label: "Denied" },
  { value: "expired", label: "Expired" },
  { value: "violated", label: "Violated" },
  { value: "renewed", label: "Renewed" },
  { value: "dismissed", label: "Dismissed" },
];

/** Ensure any legacy value from the DB appears in the dropdown */
function ensureOption(
  options: { value: string; label: string }[],
  value: string | null | undefined,
): { value: string; label: string }[] {
  if (!value) return options;
  const lower = value.toLowerCase();
  if (options.some((o) => o.value === lower || o.value === value)) return options;
  const label = value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return [...options, { value, label }];
}

/** Normalize a DB value to match dropdown option values (lowercase + underscored) */
function normalizeValue(val: string | null | undefined): string {
  if (!val) return "";
  const lower = val.toLowerCase().replace(/\s+/g, "_");
  return lower;
}

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  pending: "bg-amber-100 text-amber-800",
  granted: "bg-green-100 text-green-800",
  denied: "bg-red-100 text-red-800",
  expired: "bg-gray-100 text-gray-800",
  violated: "bg-red-100 text-red-800",
  renewed: "bg-blue-100 text-blue-800",
  dismissed: "bg-gray-100 text-gray-800",
};

function getExpirationWarning(expirationDate: string | null): {
  level: "none" | "yellow" | "red" | "expired";
  label: string;
} {
  if (!expirationDate) return { level: "none", label: "" };

  const now = new Date();
  const exp = new Date(expirationDate);
  const daysUntil = Math.ceil(
    (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (daysUntil < 0) return { level: "expired", label: "Expired" };
  if (daysUntil <= 7)
    return { level: "red", label: `Expires in ${daysUntil} day${daysUntil === 1 ? "" : "s"}` };
  if (daysUntil <= 30)
    return { level: "yellow", label: `Expires in ${daysUntil} days` };
  return { level: "none", label: "" };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ProtectionOrdersListProps {
  personId: string;
}

export function ProtectionOrdersList({ personId }: ProtectionOrdersListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);

  const { data, isLoading } = useQuery<{ data: ProtectionOrderRecord[] }>({
    queryKey: ["protectionOrders", personId],
    queryFn: () =>
      api.get<{ data: ProtectionOrderRecord[] }>(
        `/clients/${personId}/protection-orders`,
      ),
    enabled: !!personId,
  });

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/clients/${personId}/protection-orders`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["protectionOrders", personId],
      });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      api.put(`/clients/${personId}/protection-orders/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["protectionOrders", personId],
      });
      setEditingOrderId(null);
    },
  });

  const orders = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Protection Orders</h3>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => {
            setShowForm(true);
            setEditingOrderId(null);
          }}
        >
          <Plus className="h-4 w-4" />
          Add Order
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">New Protection Order</CardTitle>
          </CardHeader>
          <CardContent>
            <ProtectionOrderForm
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
              error={createMutation.isError ? (() => { const e = createMutation.error as any; const msg = e?.body?.error ?? e?.body?.message ?? "Failed to create order"; const details = e?.body?.details; return details?.length ? `${msg}: ${details.join(", ")}` : msg; })() : undefined}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {!isLoading && orders.length === 0 && !showForm && (
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No protection orders found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        orders.map((order) => {
          const isEditing = editingOrderId === order.id;
          const warning = getExpirationWarning(order.expiration_date);
          const staffName =
            order.staff_first_name && order.staff_last_name
              ? `${order.staff_first_name} ${order.staff_last_name}`
              : null;

          // Show inline edit form instead of the card content
          if (isEditing) {
            return (
              <Card key={order.id} className="border-primary/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Edit Protection Order</CardTitle>
                </CardHeader>
                <CardContent>
                  <ProtectionOrderForm
                    initial={order}
                    onSubmit={(d) =>
                      updateMutation.mutate({ id: order.id, ...d })
                    }
                    onCancel={() => setEditingOrderId(null)}
                    isLoading={updateMutation.isPending}
                    error={updateMutation.isError ? (() => { const e = updateMutation.error as any; const msg = e?.body?.error ?? e?.body?.message ?? "Failed to update order"; const details = e?.body?.details; return details?.length ? `${msg}: ${details.join(", ")}` : msg; })() : undefined}
                  />
                </CardContent>
              </Card>
            );
          }

          return (
            <Card
              key={order.id}
              className={cn(
                warning.level === "red" && "border-red-300",
                warning.level === "expired" && "border-red-400 bg-red-50",
                warning.level === "yellow" && "border-amber-300",
              )}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium flex items-center gap-2 capitalize">
                      {order.order_type
                        ? order.order_type.replace(/_/g, " ")
                        : "Protection Order"}
                      {(warning.level === "red" ||
                        warning.level === "expired") && (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      )}
                      {warning.level === "yellow" && (
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                      )}
                    </CardTitle>
                    <CardDescription className="mt-0.5">
                      {order.filed_date &&
                        `Filed: ${new Date(order.filed_date).toLocaleDateString()}`}
                      {order.granted_date &&
                        ` | Granted: ${new Date(order.granted_date).toLocaleDateString()}`}
                      {order.expiration_date &&
                        ` | Expires: ${new Date(order.expiration_date).toLocaleDateString()}`}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                        statusColors[normalizeValue(order.status) || "pending"] ??
                          statusColors.pending,
                      )}
                    >
                      {order.status?.replace(/_/g, " ") ?? "pending"}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingOrderId(order.id);
                        setShowForm(false);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {warning.label && (
                  <p
                    className={cn(
                      "text-xs font-medium mb-1",
                      warning.level === "red" || warning.level === "expired"
                        ? "text-red-600"
                        : "text-amber-600",
                    )}
                  >
                    {warning.label}
                  </p>
                )}
                {order.details && (
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {formatJsonDetails(order.details)}
                  </p>
                )}
                {staffName && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Staff assisted: {staffName}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

interface ProtectionOrderFormProps {
  initial?: ProtectionOrderRecord;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  isLoading: boolean;
  error?: string;
}

function formatJsonDetails(raw: string): string {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) {
      return Object.entries(parsed)
        .filter(([, v]) => v !== null && v !== undefined && v !== "")
        .map(([key, value]) => {
          const label = key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
          if (Array.isArray(value)) {
            return `${label}: ${value.map((v) => String(v).replace(/_/g, " ")).join(", ")}`;
          }
          return `${label}: ${String(value)}`;
        })
        .join("\n");
    }
    return raw;
  } catch {
    return raw;
  }
}

function ProtectionOrderForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
  error,
}: ProtectionOrderFormProps) {
  const initialOrderType = normalizeValue(initial?.order_type);
  const initialStatus = normalizeValue(initial?.status) || "pending";

  const [orderType, setOrderType] = useState(initialOrderType);
  const [status, setStatus] = useState(initialStatus);
  const [filedDate, setFiledDate] = useState(initial?.filed_date || "");
  const [grantedDate, setGrantedDate] = useState(initial?.granted_date || "");
  const [expirationDate, setExpirationDate] = useState(
    initial?.expiration_date || "",
  );
  const [details, setDetails] = useState(() => {
    const raw = initial?.details || "";
    return formatJsonDetails(raw);
  });

  // Ensure legacy DB values appear in the dropdown lists
  const orderTypeOptions = ensureOption(baseOrderTypeOptions, initialOrderType);
  const statusOptions = ensureOption(baseStatusOptions, initialStatus);

  function handleSubmit() {
    const data: Record<string, unknown> = {};
    if (orderType) data.orderType = orderType;
    if (status) data.status = status;
    if (filedDate) data.filedDate = filedDate;
    if (grantedDate) data.grantedDate = grantedDate;
    if (expirationDate) data.expirationDate = expirationDate;
    if (details) data.details = details;
    onSubmit(data);
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Select
          label="Order Type"
          options={orderTypeOptions}
          value={orderType}
          onChange={setOrderType}
          placeholder="Select type..."
        />
        <Select
          label="Status"
          options={statusOptions}
          value={status}
          onChange={setStatus}
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Input
          label="Filed Date"
          type="date"
          value={filedDate}
          onChange={(e) => setFiledDate(e.target.value)}
        />
        <Input
          label="Granted Date"
          type="date"
          value={grantedDate}
          onChange={(e) => setGrantedDate(e.target.value)}
        />
        <Input
          label="Expiration Date"
          type="date"
          value={expirationDate}
          onChange={(e) => setExpirationDate(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Details</label>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={3}
          placeholder="Order details, conditions..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="flex gap-3">
        <Button size="sm" onClick={handleSubmit} loading={isLoading}>
          {initial ? "Update Order" : "Create Order"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
